import { useLazyQuery, useMutation } from '@apollo/client';
import { ArrowDownCircleIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { CompanyEventEnumType, CompanyType } from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';
import CompanySelectInput from 'routes/Company/Events/CompanySelectInput';

import Button from 'primitives/Button';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import constants from 'utils/constants';

import { InvestorRightsOptions } from './constants';

const CREATE_COMPANY_EVENT_MUTATION = gql(`
  mutation CreateCompanyEventFromWebhook(
    $companyId: ID!,
    $label: String!,
    $type: CompanyEventEnumType!,
    $eventDate: String!,
    $isAuthorizationRequired: Boolean!,
    $description: String,
    $notes: String,
    $investorRightsAdded: [InvestorRightEnumType!],
    $investorRightsRemoved: [InvestorRightEnumType!],
    $shareAssets: [ID!],
    $convertibleAssets: [ID!],
    $deals: [ID!],
    $documents: [Upload!]
    $resolveWebhookEventId: ID
  ) {
    createCompanyEvent(
      companyId: $companyId,
      label: $label,
      type: $type,
      eventDate: $eventDate,
      isAuthorizationRequired: $isAuthorizationRequired,
      description: $description,
      notes: $notes,
      investorRightsAdded: $investorRightsAdded,
      investorRightsRemoved: $investorRightsRemoved,
      shareAssets: $shareAssets,
      convertibleAssets: $convertibleAssets,
      deals: $deals,
      documents: $documents,
      resolveWebhookEventId: $resolveWebhookEventId
    ) {
      id
      label
      type
      eventDate
      isAuthorizationRequired
      authorizationStatus
      description
      notes
      investorRightsAdded
      investorRightsRemoved
    }
  }
`);

const GET_GMAIL_MESSAGES_BY_SENDER = gql(`
  query GetGmailMessagesBySender($senderId: String!, $maxResults: Int) {
    gmailMessagesBySender(senderId: $senderId, maxResults: $maxResults) {
      nodes {
        id
        threadId
        snippet
        subject
        from
        date
        body
        attachments {
          filename
          mimeType
          size
          data
        }
      }
    }
  }
`);

const GET_COMPANY_ASSETS = gql(`
  query GetCompanyAssetsForWebhook($companyId: ID!) {
    company(id: $companyId) {
      id
      shareClasses {
        id
        name
        shareAssets {
          id
          status
          numberOfShares
          deal {
            id
            schemeName
          }
        }
      }
      convertibleClasses {
        id
        name
        convertibleAssets {
          id
          status
          value
          deal {
            id
            schemeName
          }
        }
      }
      deals {
        id
        schemeName
      }
    }
  }
`);

interface CreateCompanyEventDialogProps {
  open: boolean;
  onClose: () => void;
  senderId: string;
  messageId?: string;
  threadId?: string;
  webhookId?: string;
}

const getBlobFromBase64 = attachment => {
  const base64Fixed = attachment.data.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '');

  const pad = base64Fixed.length % 4;
  const paddedBase64 = pad ? base64Fixed + '='.repeat(4 - pad) : base64Fixed;

  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: attachment.mimeType });
};

const downloadAttachment = attachment => {
  const blob = getBlobFromBase64(attachment);
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const CreateCompanyEventFromWebhookDialogButton: FC<CreateCompanyEventDialogProps> = ({
  open,
  onClose,
  webhookId,
  senderId,
}) => {
  const [selectedCompany, setSelectedCompany] = useState<CompanyType>();
  const [createCompanyEvent, { loading: createLoading, error: createError }] = useMutation(
    CREATE_COMPANY_EVENT_MUTATION,
    {
      onCompleted: () => {
        onClose();
      },
    }
  );

  const [
    fetchGmailMessagesBySender,
    { loading: messagesLoading, data: messagesData, error: messagesError },
  ] = useLazyQuery(GET_GMAIL_MESSAGES_BY_SENDER, {
    variables: {
      senderId: constants.AL_INDIA_HS_EMAIL,
      maxResults: 1,
    },
  });

  const [getCompanyAssets, { loading: assetsLoading, data: assetsData, error: assetsError }] =
    useLazyQuery(GET_COMPANY_ASSETS);

  useEffect(() => {
    if (!selectedCompany) return;
    getCompanyAssets({
      variables: {
        companyId: selectedCompany.id,
      },
    });
  }, [selectedCompany, getCompanyAssets]);

  useEffect(() => {
    if (!senderId) return;
    fetchGmailMessagesBySender();
  }, [senderId, fetchGmailMessagesBySender]);

  const assets = assetsData?.company
    ? [
        ...(assetsData.company.shareClasses?.flatMap(sc =>
          sc.shareAssets.map(asset => ({
            id: asset.id,
            name: `${sc.name} - ${asset.numberOfShares} shares`,
            schemeName: asset.deal?.schemeName,
            type: 'SHARE_ASSET' as const,
          }))
        ) || []),
        ...(assetsData.company.convertibleClasses?.flatMap(cc =>
          cc.convertibleAssets.map(asset => ({
            id: asset.id,
            name: `${cc.name} - ${asset.value}`,
            schemeName: asset.deal?.schemeName,
            type: 'CONVERTIBLE_ASSET' as const,
          }))
        ) || []),
        ...(assetsData.company.deals
          ?.map(deal => {
            const allShareAndConvertibleAssets = [
              ...(assetsData.company.shareClasses?.flatMap(sc => sc.shareAssets) ?? []),
              ...(assetsData.company.convertibleClasses?.flatMap(cc => cc.convertibleAssets) ?? []),
            ];
            const doesDealHaveAsset = allShareAndConvertibleAssets.some(
              asset => asset?.deal?.id === deal.id
            );

            if (doesDealHaveAsset) return null;

            return {
              id: deal.id,
              name: 'Asset Not Created Yet',
              schemeName: deal.schemeName,
              type: 'DEAL' as const,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null) || []),
      ]
    : [];

  if (messagesLoading) return <LoadingIndicator />;

  if ((messagesError || !messagesData) && senderId) return <ErrorMessage error={messagesError} />;

  if (!messagesData?.gmailMessagesBySender?.nodes?.length) return <></>;

  const messages = messagesData.gmailMessagesBySender.nodes;
  const attachments = messages
    .flatMap(
      message =>
        message.attachments?.map(attachment => {
          if (!attachment) return null;
          const blob = getBlobFromBase64(attachment);
          return new File([blob], attachment.filename || 'File', { type: blob.type });
        }) || []
    )
    .filter(Boolean);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogPanel className="min-w-[800px]">
        <DialogTitle>Create an Event</DialogTitle>
        {!assetsLoading ? (
          <FormPanel
            loading={createLoading || assetsLoading}
            error={createError || assetsError}
            // disabled={createLoading || assetsLoading || !selectedCompany}
            onCancel={onClose}
            onSubmit={async data => {
              if (!selectedCompany) return;

              await createCompanyEvent({
                variables: {
                  companyId: selectedCompany.id,
                  type: data.type,
                  eventDate: data.eventDate,
                  label: data.label,
                  description: data.description,
                  isAuthorizationRequired: data.isAuthorizationRequired || false,
                  notes: data.notes,
                  investorRightsAdded: data.investorRightsAdded,
                  investorRightsRemoved: data.investorRightsRemoved,
                  shareAssets:
                    data.assetIds?.filter(
                      id => assets.find(asset => asset.id === id)?.type === 'SHARE_ASSET'
                    ) || [],
                  convertibleAssets:
                    data.assetIds?.filter(
                      id => assets.find(asset => asset.id === id)?.type === 'CONVERTIBLE_ASSET'
                    ) || [],
                  deals:
                    data.assetIds?.filter(
                      id => assets.find(asset => asset.id === id)?.type === 'DEAL'
                    ) || [],
                  documents: data.documents || [],
                  resolveWebhookEventId: webhookId,
                },
              });
            }}
          >
            <div className="mt-4 p-6 bg-gray-50 rounded-lg col-span-full">
              <h3 className="font-medium text-lg mb-8">Last 3 emails from {senderId}</h3>
              <div className="space-y-6">
                {messages.map((message, index) => (
                  <div key={message.id} className={`${index > 0 ? 'border-t pt-4' : ''}`}>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-medium">From:</span> {message.from}
                      </p>
                      <p>
                        <span className="font-medium">Subject:</span> {message.subject}
                      </p>
                      <p>
                        <span className="font-medium">Date:</span> {message.date}
                      </p>
                      <p>
                        <span className="font-medium">Message:</span>
                        <div className="mt-2 text-sm text-gray-600">{message.snippet}</div>
                      </p>
                      {message.attachments && message.attachments.length > 0 && (
                        <div>
                          <span className="font-medium">Attachments:</span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {message.attachments.map((attachment, i) => {
                              if (!attachment) return null;
                              return (
                                <Button
                                  key={i}
                                  onClick={() => downloadAttachment(attachment)}
                                  className="mr-2 mb-2 text-sm py-1 px-2"
                                  variant="secondary"
                                  leadingIcon={<ArrowDownCircleIcon />}
                                >
                                  {attachment.filename}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <FormInput
              type="custom"
              fieldName="company"
              customInput={CompanySelectInput}
              label="Select Company"
              defaultValue={selectedCompany ?? ''}
              validators={{ required: true }}
              onChange={value => {
                setSelectedCompany(value.company);
              }}
            />

            {/* Conditional fields */}
            <FormInput
              type="select"
              fieldName="type"
              label="Type"
              defaultValue={CompanyEventEnumType.TransactionalDocuments}
              options={[
                {
                  value: CompanyEventEnumType.TransactionalDocuments,
                  label: 'Transactional Documents',
                },
                { value: CompanyEventEnumType.AncillaryDocuments, label: 'Ancillary Documents' },
                {
                  value: CompanyEventEnumType.RegulatoryDocuments,
                  label: 'Regulatory Documents',
                },
                { value: CompanyEventEnumType.Others, label: 'Others' },
              ]}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
              validators={{ required: true }}
            />
            <FormInput
              type="text"
              fieldName="label"
              label="Label"
              defaultValue=""
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
              validators={{ required: true }}
            />
            <FormInput
              type="date"
              fieldName="eventDate"
              label="Event Date"
              defaultValue=""
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
              validators={{ required: true }}
            />
            <FormInput
              type="text"
              fieldName="description"
              label="Description"
              defaultValue=""
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />
            <FormInput
              type="textarea"
              fieldName="notes"
              label="Notes"
              defaultValue=""
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />
            <FormInput
              type="switch"
              fieldName="isAuthorizationRequired"
              label="Authorization Required"
              helperText="If the event requires authorization from associated GPs, please select this option."
              defaultValue={false}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />

            <FormInput
              type="multi_select"
              compact
              fieldName="investorRightsAdded"
              label="Investor Rights Added"
              defaultValue={[]}
              options={InvestorRightsOptions}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />
            <FormInput
              type="multi_select"
              compact
              fieldName="investorRightsRemoved"
              label="Investor Rights Removed"
              defaultValue={[]}
              options={InvestorRightsOptions}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />
            <FormInput
              type="upload"
              fieldName="documents"
              label="Documents"
              defaultValue={attachments}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />

            {/* Conditional on assets */}
            <FormInput
              type="checkbox-group"
              fieldName="assetIds"
              label="Assets"
              compact
              defaultValue={assets.map(asset => asset.id)}
              options={assets.map(({ id, name, schemeName }) => ({
                value: id,
                label: `${name} (${schemeName || ''})`,
              }))}
              validators={{ required: true }}
              conditionsToShow={{
                matches: [{ field: 'company', condition: '!==', value: '' }],
              }}
            />

            <div className="mb-12" />
          </FormPanel>
        ) : (
          <LoadingIndicator />
        )}
      </DialogPanel>
    </Dialog>
  );
};

export default CreateCompanyEventFromWebhookDialogButton;
